import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  COMPARISON_OPERATORS,
  Checkbox,
  ConditionRow,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchField,
  SegmentedControl,
  Skeleton,
  cn,
  type Condition,
  type ConditionField,
} from "@fox1ck-org/ui";
import { DimensionFilterOp, MetricFilterOp } from "@gen/tracker/v1/reporting_pb";

import { formatInt } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useReport } from "@/lib/services/report-service";
import type { DimFilter, MetricCond, ReportFilters } from "@/lib/report";
import { mergeFilters } from "./state";
import { reportColumns, columnLabel } from "./columns";
import { SPEND_WITHOUT_CLICKS, dimCount, useDimensionNames } from "./names";
import type { ReportEngine } from "./engine";

/** The dimensions a buyer narrows by first (доска ReportFilter), then the rest of the schema. */
const FIRST_DIMS = ["country", "source", "owner", "landing", "offer", "creative", "traffic_class", "sub1"];
const FILTER_METRICS = ["profit", "cost", "roi", "cr", "approve_rate", "bots_pct", "clicks", "leads"];

const OP_TO_PROTO: Record<string, MetricFilterOp> = {
  lt: MetricFilterOp.LT,
  lte: MetricFilterOp.LTE,
  gt: MetricFilterOp.GT,
  gte: MetricFilterOp.GTE,
  eq: MetricFilterOp.EQ,
  neq: MetricFilterOp.NEQ,
};
const OP_FROM_PROTO = Object.fromEntries(Object.entries(OP_TO_PROTO).map(([k, v]) => [v, k])) as Record<number, string>;
export const METRIC_OP_SIGN: Record<number, string> = Object.fromEntries(COMPARISON_OPERATORS.map((o) => [OP_TO_PROTO[o.value]!, o.label]));

/** «Страна RO, PL», «Страна не RO» — a chip's operator in words. */
export function DIM_OP_WORD(t: ReturnType<typeof useT>, op: DimensionFilterOp): string {
  switch (op) {
    case DimensionFilterOp.NOT_IN:
      return `${t("rep.filter.notIn")} `;
    case DimensionFilterOp.CONTAINS:
      return `${t("rep.filter.contains")} `;
    case DimensionFilterOp.NOT_CONTAINS:
      return `${t("rep.filter.notContains")} `;
    default:
      return "";
  }
}

const isPct = (format: string | undefined) => format === "pct0" || format === "pct1" || format === "roi";

/**
 * «+ Фильтр» (доска ReportFilter): a draft of every filter at once — a
 * dimension's values picked from what the period actually has (the number on
 * the right is its clicks), and the metric conditions on the package's
 * ConditionRow. «Применить» writes them to the address; «Найдено …» is the
 * server's count under the draft.
 */
export function FilterPanel({ engine }: { engine: ReportEngine }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ReportFilters>(engine.state.filters);
  const [active, setActive] = useState<string>("country");
  const onOpen = (o: boolean) => {
    if (o) {
      setDraft(engine.state.filters);
      setActive(engine.state.filters.dims[0]?.key ?? "country");
    }
    setOpen(o);
  };
  const dims = useMemo(() => {
    const known = (engine.schema?.dimensions ?? []).filter((d) => d.drilldownable && !engine.hiddenDims.includes(d.key));
    const first = FIRST_DIMS.flatMap((k) => known.filter((d) => d.key === k));
    return [...first, ...known.filter((d) => !FIRST_DIMS.includes(d.key))];
  }, [engine.schema, engine.hiddenDims]);
  const cols = reportColumns(engine.schema);
  const metricFields: ConditionField[] = FILTER_METRICS.flatMap((k) => {
    const c = cols.find((x) => x.key === k);
    if (!c) return [];
    return [{ value: k, label: columnLabel(t, c), kind: "number" as const, unit: isPct(c.format) ? "%" : c.band === "money" ? "USD" : undefined }];
  });

  const apply = () => {
    engine.state.setFilters(draft);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex h-7 shrink-0 items-center rounded-full border border-dashed border-border-strong px-3 text-sm text-muted-foreground hover:text-foreground">
          + {t("rep.filter")}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-160 flex-col p-0">
        {open ? (
          <>
            <div className="grid h-125 grid-cols-[--spacing(45)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)]">
              <nav className="min-h-0 overflow-y-auto border-r border-border p-2" aria-label={t("rep.filter")}>
                <Heading>{t("rep.filter.dims")}</Heading>
                {dims.map((d) => (
                  <SideItem key={d.key} active={active === d.key} marked={draft.dims.some((f) => f.key === d.key)} onClick={() => setActive(d.key)}>
                    {d.label}
                  </SideItem>
                ))}
                <Heading className="mt-3">{t("rep.filter.metrics")}</Heading>
                {metricFields.map((m) => (
                  <SideItem
                    key={m.value}
                    marked={draft.metrics.some((c) => c.key === m.value)}
                    onClick={() => !draft.metrics.some((c) => c.key === m.value) && setDraft({ ...draft, metrics: [...draft.metrics, { key: m.value, op: MetricFilterOp.LT, value: "" }] })}
                  >
                    {m.label}
                  </SideItem>
                ))}
              </nav>
              <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-3">
                <DimValues engine={engine} dim={active} draft={draft} onChange={setDraft} />
                <div className="flex flex-col gap-2 border-t border-border pt-3">
                  <Heading>{t("rep.filter.byMetric")}</Heading>
                  {draft.metrics.map((m, i) => (
                    <ConditionRow
                      key={i}
                      fields={metricFields}
                      operators={COMPARISON_OPERATORS}
                      value={toCondition(m, cols)}
                      onChange={(next) => setDraft({ ...draft, metrics: draft.metrics.map((x, j) => (j === i ? fromCondition(next, cols) : x)) })}
                      onRemove={() => setDraft({ ...draft, metrics: draft.metrics.filter((_, j) => j !== i) })}
                      columns="minmax(0,1fr) 4.5rem 8rem"
                      labels={{ field: t("rep.filter.metric"), operator: t("rep.filter.op"), value: t("rep.filter.value"), remove: t("rep.filter.removeCondition") }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, metrics: [...draft.metrics, { key: metricFields[0]?.value ?? "profit", op: MetricFilterOp.LT, value: "" }] })}
                    className="inline-flex items-center gap-1 self-start text-sm text-primary hover:underline"
                  >
                    <Plus className="size-3.5" /> {t("rep.filter.addCondition")}
                  </button>
                  <p className="text-xs text-muted-foreground">{t("rep.filter.where")}</p>
                </div>
              </div>
            </div>
            <footer className="flex items-center gap-2 border-t border-border px-3 py-2.5">
              <Found engine={engine} draft={draft} />
              <span className="flex-1" />
              <Button variant="ghost" size="sm" onClick={() => setDraft({ dims: [], metrics: [] })}>
                {t("rep.filter.reset")}
              </Button>
              <Button size="sm" onClick={apply} disabled={draft.metrics.some((m) => m.value === "" || !Number.isFinite(Number(m.value)))}>
                {t("rep.filter.apply")}
              </Button>
            </footer>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function toCondition(m: MetricCond, cols: ReturnType<typeof reportColumns>): Condition {
  const pct = isPct(cols.find((c) => c.key === m.key)?.format);
  const n = Number(m.value);
  return { field: m.key, operator: OP_FROM_PROTO[m.op] ?? "lt", value: m.value === "" || !Number.isFinite(n) ? "" : String(pct ? Math.round(n * 10000) / 100 : n) };
}

function fromCondition(c: Condition, cols: ReturnType<typeof reportColumns>): MetricCond {
  const pct = isPct(cols.find((x) => x.key === c.field)?.format);
  const raw = typeof c.value === "string" ? c.value.replace(",", ".").trim() : "";
  const n = Number(raw);
  // A percent is typed in percent and sent as a ratio.
  return { key: c.field, op: OP_TO_PROTO[c.operator] ?? MetricFilterOp.LT, value: raw === "" || !Number.isFinite(n) ? raw : String(pct ? n / 100 : n) };
}

/** One dimension's values in the period, by clicks, with «входит в | не входит». */
function DimValues({ engine, dim, draft, onChange }: { engine: ReportEngine; dim: string; draft: ReportFilters; onChange: (f: ReportFilters) => void }) {
  const t = useT();
  const [q, setQ] = useState("");
  const p = engine.state.params;
  const current = draft.dims.find((f) => f.key === dim);
  const op = current?.op === DimensionFilterOp.NOT_IN ? DimensionFilterOp.NOT_IN : DimensionFilterOp.IN;
  const others = mergeFilters(engine.pinned, { dims: draft.dims.filter((f) => f.key !== dim), metrics: [] });
  const values = useReport(
    { from: p.from, to: p.to, timezone: p.tz, streamIds: engine.streamIds, dims: [dim], basis: engine.basis, filters: others, subtotals: false, sort: { key: "clicks", desc: true }, limit: 60 },
    !!engine.streamIds && !engine.noCampaigns,
  );
  const rows = (values.data?.rows ?? []).filter((r) => !r.spendWithoutClicks && r.dimensionPath[0] !== SPEND_WITHOUT_CLICKS);
  const names = useDimensionNames(rows.map((r) => [dim, r.dimensionPath[0] ?? ""] as const));
  const max = Math.max(1, ...rows.map((r) => statsRowToNumbers(r.stats).clicks ?? 0));
  const picked = new Set(current?.values ?? []);
  const shown = rows.filter((r) => !q || names.name(dim, r.dimensionPath[0] ?? "").toLowerCase().includes(q.toLowerCase()));
  const label = engine.dimLabel(dim);

  const write = (nextValues: string[], nextOp = op) => {
    const rest = draft.dims.filter((f) => f.key !== dim);
    const f: DimFilter[] = nextValues.length ? [...rest, { key: dim, op: nextOp, values: nextValues }] : rest;
    onChange({ ...draft, dims: f });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <SegmentedControl
          size="sm"
          aria-label={t("rep.filter.op")}
          value={op === DimensionFilterOp.NOT_IN ? "nin" : "in"}
          onValueChange={(v) => write([...picked], v === "nin" ? DimensionFilterOp.NOT_IN : DimensionFilterOp.IN)}
          options={[
            { value: "in", label: t("rep.filter.in") },
            { value: "nin", label: t("rep.filter.notInLong") },
          ]}
        />
        <SearchField value={q} onChange={setQ} debounceMs={0} placeholder={t("rep.filter.find", { what: label.toLowerCase() })} label={t("rep.filter.find", { what: label.toLowerCase() })} className="flex-1" />
      </div>
      <ul className="flex max-h-60 flex-col overflow-y-auto">
        {values.isLoading ? <li className="px-1 py-2 text-xs text-muted-foreground">{t("rep.loading")}</li> : null}
        {!values.isLoading && shown.length === 0 ? <li className="px-1 py-2 text-xs text-muted-foreground">{t("rep.nothingFound")}</li> : null}
        {shown.map((r) => {
          const v = r.dimensionPath[0] ?? "";
          const clicks = statsRowToNumbers(r.stats).clicks;
          return (
            <li key={v}>
              <label className="flex h-7.5 cursor-pointer items-center gap-2.5 rounded px-1 text-sm hover:bg-surface-2">
                <Checkbox checked={picked.has(v)} onCheckedChange={(c) => write(c === true ? [...picked, v] : [...picked].filter((x) => x !== v))} />
                {names.pending(dim, v) ? (
                  <Skeleton className="h-3 min-w-0 flex-1" aria-label={t("rep.naming")} />
                ) : (
                  <span className="min-w-0 flex-1 truncate">{names.name(dim, v)}</span>
                )}
                <span aria-hidden className="h-1 w-17.5 overflow-hidden rounded bg-surface-2">
                  <span className="block h-full rounded bg-muted-foreground" style={{ width: `${((clicks ?? 0) / max) * 100}%` }} />
                </span>
                <span className="w-14 text-right font-mono text-xs tabular-nums text-muted-foreground">{formatInt(clicks)}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">{t("rep.filter.valuesNote", { n: picked.size })}</p>
    </div>
  );
}

/** «Найдено 4 кампании»: how many top-level groups pass the draft, counted by the server. */
function Found({ engine, draft }: { engine: ReportEngine; draft: ReportFilters }) {
  const t = useT();
  const p = engine.state.params;
  const top = engine.state.dims[0]!;
  const q = useReport(
    { from: p.from, to: p.to, timezone: p.tz, streamIds: engine.streamIds, dims: [top], basis: engine.basis, filters: mergeFilters(engine.pinned, draft), subtotals: false, limit: 1 },
    !!engine.streamIds && !engine.noCampaigns && !draft.metrics.some((m) => m.value === ""),
  );
  const n = q.data?.total;
  return (
    <span className="text-xs text-muted-foreground">
      {n === undefined ? t("rep.loading") : t("rep.filter.found", { what: dimCount(t, top, n, engine.dimLabel(top), formatInt(n)) })}
    </span>
  );
}

function Heading({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("px-2 pb-1 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground", className)}>{children}</div>;
}

function SideItem({ children, active, marked, onClick }: { children: React.ReactNode; active?: boolean; marked: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={cn("flex h-7.25 w-full items-center gap-2 rounded px-2 text-left text-sm", active ? "bg-surface-2 font-medium" : "hover:bg-surface-2")}>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {marked ? <span aria-hidden className="size-1.5 rounded-full bg-primary" /> : null}
    </button>
  );
}
